<script setup>
/** @typedef {import('../../domain/entities/AuditTask.types').AuditTask} AuditTask */

import { i18n } from 'boot/i18n';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import { Timestamp } from 'firebase/firestore';
import { useTime } from 'src/composables/time';

import { TASK_STATUSES, VERDICTS } from '../../domain/constants/auditResults';
import { AUDIT_TASK_ERROR_CODES } from '../../domain/constants/auditTaskErrorCodes';
import { useViewAuditTaskDetails } from '../composables/useViewAuditTaskDetails';

defineOptions({ name: 'AuditResultsTable' });

const props = defineProps({
  auditTasks: {
    /** @type {import('vue').PropType<AuditTask[]>} */
    type: Array,
    required: true,
  },
  isRunning: {
    type: Boolean,
    required: true,
  },
});

const { viewAuditTaskDetails } = useViewAuditTaskDetails(() => props.auditTasks);
const { getLocaleString } = useTime();

const columns = [
  {
    name: 'taskStatus',
    label: '',
    field: 'taskStatus',
    align: 'center',
  },
  {
    name: 'userName',
    label: i18n.t('noteAuditor.auditResultsTable.columns.userName'),
    field: (row) => row.sessionActivity.userName,
    align: 'left',
  },
  {
    name: 'startTimestamp',
    label: i18n.t('noteAuditor.auditResultsTable.columns.startTimestamp'),
    align: 'left',
    field: (row) => row.sessionActivity.startTimestamp,
    format: (startTimestamp) => ((startTimestamp instanceof Timestamp)
      ? getLocaleString(startTimestamp.toDate())
      : ''),
  },
  {
    name: 'endTimestamp',
    label: i18n.t('noteAuditor.auditResultsTable.columns.endTimestamp'),
    align: 'left',
    field: (row) => row.sessionActivity.endTimestamp,
    format: (endTimestamp) => ((endTimestamp instanceof Timestamp)
      ? getLocaleString(endTimestamp.toDate())
      : ''),
  },
  {
    name: 'sessionName',
    label: i18n.t('noteAuditor.auditResultsTable.columns.sessionName'),
    align: 'left',
    field: (row) => row.sessionActivity.sessionName,
  },
];

function getTaskErrorText(task) {
  if (task.errorCode === AUDIT_TASK_ERROR_CODES.INVALID_SESSION_ACTIVITY_STATUS) {
    return i18n.t('noteAuditor.errors.invalidSessionActivityStatus');
  }

  if (task.errorCode === AUDIT_TASK_ERROR_CODES.AI_UNAVAILABLE) {
    return i18n.t('common.errors.aiUnavailable');
  }

  return i18n.t('common.errors.generic');
}

</script>
<template>
  <q-table
    :rows="props.auditTasks"
    :columns="columns"
    :pagination="{
      rowsPerPage: 50,
    }"
    :loading="props.isRunning"
    flat
    class="col-12"
    @row-click="(_, row) => viewAuditTaskDetails(row)"
  >
    <template #body-cell-taskStatus="cellProps">
      <q-td :props="cellProps">
        <template v-if="cellProps.row.taskStatus === TASK_STATUSES.COMPLETED">
          <q-icon
            v-if="cellProps.row.verdict === VERDICTS.PASS"
            name="done"
            size="sm"
            color="positive"
          />
          <q-icon
            v-else-if="cellProps.row.verdict === VERDICTS.FAIL"
            name="priority_high"
            size="sm"
            color="negative"
          />
          <q-icon
            v-else-if="cellProps.row.verdict === VERDICTS.FLAG"
            name="flag"
            size="sm"
            color="orange"
          />
        </template>
        <q-icon
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.PENDING"
          name="pending"
          size="sm"
          color="grey-5"
        />
        <universal-tooltip
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.FAILED"
          show-tooltip
          :text="getTaskErrorText(cellProps.row)"
        >
          <q-icon
            name="running_with_errors"
            size="sm"
            color="negative"
          />
        </universal-tooltip>
        <q-spinner
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.PROCESSING"
          size="sm"
          color="primary"
        />
      </q-td>
    </template>
  </q-table>
</template>
